/** Curated Starlink customs evidence from the SPCX/trade warehouse. Not 10-Q mix. */

export type TradeFamily =
  | 'consumer_kit'
  | 'mini_kit'
  | 'router'
  | 'cable'
  | 'ethernet_adapter'
  | 'mount';

export type TradeShipment = {
  date: string | null;
  destination: string;
  family: TradeFamily;
  description: string;
  quantity: number | null;
  quantityUnit: string | null;
  valueUsd: number | null;
  freightUsd: number | null;
  blNumber: string | null;
  sourceLabel: string;
  sourceUrl: string;
};

export type TradeCorridor = {
  partner: string;
  y2022: number;
  y2024: number;
  labeled: boolean;
};

export const TRADE_FAMILY_LABEL: Record<TradeFamily, string> = {
  consumer_kit: 'Consumer kit',
  mini_kit: 'Mini',
  router: 'Router',
  cable: 'Cable',
  ethernet_adapter: 'Ethernet adapter',
  mount: 'Mount',
};

export const STARLINK_TRADE_CAVEAT =
  'Labeled bills name Starlink kits. They are Connectivity Products landings, not Consumer service mix. UN Comtrade HS 851762 (US reporter 842) mixes Starlink with all other data-transmission gear.';

export const STARLINK_TRADE_KPIS = [
  { value: '26', label: 'Labeled public lines', detail: 'NBD, TradeData.Pro, ImportGenius' },
  { value: '4', label: 'Kit destinations', detail: 'Ecuador, Nigeria, Ghana, Lesotho' },
  { value: '1,024', label: 'Mini kits, one bill', detail: 'Ecuador · 29 Apr 2026 · UTA-231' },
  { value: '22,748', label: 'SpaceX customs records', detail: 'NBD entity count, not kits-only' },
];

export const STARLINK_TRADE_CORRIDORS: TradeCorridor[] = [
  { partner: 'Nigeria', y2022: 10917349, y2024: 40192118, labeled: true },
  { partner: 'Kenya', y2022: 4037370, y2024: 32374946, labeled: false },
  { partner: 'Ghana', y2022: 4133198, y2024: 7089564, labeled: true },
  { partner: 'Peru', y2022: 91245985, y2024: 116135491, labeled: false },
  { partner: 'Colombia', y2022: 232477393, y2024: 247098618, labeled: false },
  { partner: 'Brazil', y2022: 644291152, y2024: 773806833, labeled: false },
  { partner: 'Ecuador', y2022: 46144599, y2024: 43968807, labeled: true },
  { partner: 'Lesotho', y2022: 16874, y2024: 7413, labeled: true },
];

export const STARLINK_TRADE_SHIPMENTS: TradeShipment[] = [
  {
    date: '2026-04-30',
    destination: 'Ecuador',
    family: 'consumer_kit',
    description: 'Consumer Starlink Kit US/CA Starlink Gen3 Dish Kit Type A UTA-232',
    quantity: 1,
    quantityUnit: 'units',
    valueUsd: 170.22,
    freightUsd: 494.91,
    blNumber: '000000007022470243',
    sourceLabel: 'ImportGenius Ecuador',
    sourceUrl: 'https://www.importgenius.com/ecuador/importers/starlink-ecuador-starec-c-ltda',
  },
  {
    date: '2026-04-29',
    destination: 'Ecuador',
    family: 'mini_kit',
    description: 'Mini Starlink Internet Kit — Type A (UTA-231)',
    quantity: 1024,
    quantityUnit: 'units',
    valueUsd: null,
    freightUsd: null,
    blNumber: 'S2601869295',
    sourceLabel: 'ImportGenius Ecuador',
    sourceUrl: 'https://www.importgenius.com/ecuador/importers/starlink-ecuador-starec-c-ltda',
  },
  {
    date: '2026-04-29',
    destination: 'Ecuador',
    family: 'cable',
    description: 'Standard Starlink Cable 15m (origin Vietnam)',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: '000000001467646283',
    sourceLabel: 'ImportGenius Ecuador',
    sourceUrl: 'https://www.importgenius.com/ecuador/importers/starlink-ecuador-starec-c-ltda',
  },
  {
    date: '2026-01-31',
    destination: 'Ecuador',
    family: 'router',
    description: 'Gen 3 mesh router Standard Mini',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'NBD SpaceX',
    sourceUrl: 'https://en.nbd.ltd/trader/info/UXH186946101',
  },
  {
    date: '2025-12-20',
    destination: 'Lesotho',
    family: 'consumer_kit',
    description: 'Consumer Starlink Kit',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'NBD SpaceX',
    sourceUrl: 'https://en.nbd.ltd/trader/info/UXH186946101',
  },
  {
    date: '2025-10-31',
    destination: 'Lesotho',
    family: 'mini_kit',
    description: 'Mini Starlink Internet Kit',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'NBD SpaceX',
    sourceUrl: 'https://en.nbd.ltd/trader/info/UXH186946101',
  },
  {
    date: '2025-04-29',
    destination: 'Nigeria',
    family: 'consumer_kit',
    description: 'Consumer Starlink Kit US/CA Starlink Gen3 Dish Kit',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'TradeData.Pro',
    sourceUrl:
      'https://tradedata.pro/trade-database-demo/united-states/export-data/company/space-exploration-technologies-corp-spa/',
  },
  {
    date: '2025-02-21',
    destination: 'Ghana',
    family: 'consumer_kit',
    description: 'Consumer Starlink internet device and accessories Type G (UTA-232)',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'TradeData.Pro',
    sourceUrl: 'https://tradedata.pro/trade-database-demo/united-states/export-data/hs-code/8517620000/',
  },
  {
    date: '2024-12-31',
    destination: 'Ghana',
    family: 'mini_kit',
    description: 'Mini Starlink internet device and accessories Type G (UTA-231)',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'TradeData.Pro',
    sourceUrl: 'https://tradedata.pro/trade-database-demo/united-states/export-data/hs-code/8517620000/',
  },
  {
    date: '2024-10-07',
    destination: 'Ghana',
    family: 'consumer_kit',
    description: '12 packages STC Starlink broadband internet receiver Type G (UTA-232)',
    quantity: null,
    quantityUnit: null,
    valueUsd: null,
    freightUsd: null,
    blNumber: null,
    sourceLabel: 'TradeData.Pro',
    sourceUrl: 'https://tradedata.pro/trade-database-demo/united-states/export-data/hs-code/8517620000/',
  },
];

export function formatUsdMillions(value: number): string {
  if (value >= 1e9) {
    const billions = value / 1e9;
    return `$${billions.toFixed(billions >= 10 ? 1 : 2)}b`;
  }
  if (value >= 1e6) {
    const millions = value / 1e6;
    const digits = millions >= 100 ? 0 : 1;
    return `$${millions.toFixed(digits)}m`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(value >= 10e3 ? 0 : 1)}k`;
  }
  return `$${Math.round(value)}`;
}

export function corridorChange(row: TradeCorridor): { label: string; down: boolean } {
  if (row.y2022 <= 0) return { label: 'n/a', down: false };
  const pct = ((row.y2024 / row.y2022) - 1) * 100;
  const down = pct < 0;
  const abs = Math.abs(pct);
  const shown = abs >= 10 ? abs.toFixed(0) : abs.toFixed(1);
  return { label: `${down ? '−' : '+'}${shown}%`, down };
}
